import React, { useCallback, useMemo, useState } from 'react';
import { addDays, isAfter, isBefore } from 'date-fns';
import PropTypes from 'prop-types';
import { useForm } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { InputAdornment } from '@mui/material';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';

import { WaeStandardTextField } from '../../../../components/WaeStandardTextField';
import selectUser from '../../../../store/selectors/appSelector';
import theme from '../../../../theme';

const RANGE_POSITIONS = {
  START: 'start',
  END: 'end',
};

/**
 * Shared start/end date-range picker for job creation.
 * - Uses react-final-form `input`/`meta` props
 * - Optional maxDaysAfterStart constraint (used by 2-week recurring flow)
 */
const JobDateRangePicker = ({ input, meta, maxDaysAfterStart, getStartError, getEndError }) => {
  const intl = useIntl();
  const user = useSelector(selectUser);
  const form = useForm();

  const [isOpen, setIsOpen] = useState(false);
  const [rangePosition, setRangePosition] = useState(RANGE_POSITIONS.START);

  const rangeChangeHandler = (change) => {
    input.onChange(change);
    form.blur('dateRange', true);
  };

  const startDateLabel = intl.formatMessage({ id: 'job.create.startDate' });
  const endDateLabel = intl.formatMessage({ id: 'job.create.endDate' });

  const commonPickerProps = {
    onOpen: () => setIsOpen(true),
    onClose: () => setIsOpen(false),
    disablePast: user?.role !== UserRole.ADMIN,
    calendars: 1,
    open: isOpen,
    PopperProps: {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 0],
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['bottom'],
          },
        },
      ],
    },
  };

  const startRaw = input?.value?.[0] || null;
  const startDateValue = useMemo(() => (startRaw ? new Date(startRaw) : null), [startRaw]);

  const shouldDisableDate = useCallback(
    (day, position) => {
      if (position !== RANGE_POSITIONS.END) return false;
      if (!startDateValue) return false;
      if (!maxDaysAfterStart) {
        return isBefore(day, startDateValue);
      }

      const maxEndDate = addDays(startDateValue, maxDaysAfterStart);
      return isAfter(day, maxEndDate) || isBefore(day, startDateValue);
    },
    [startDateValue, maxDaysAfterStart]
  );

  const startError = getStartError?.({ value: input?.value });
  const endError = getEndError?.({ value: input?.value });
  const shouldShowStartError = startError && (meta.submitFailed || meta.modified || meta.touched);
  const shouldShowEndError = endError && (meta.submitFailed || meta.modified || meta.touched);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      localeText={{ start: startDateLabel, end: endDateLabel }}
    >
      <DateRangePicker
        {...commonPickerProps}
        value={input?.value || [null, null]}
        onChange={rangeChangeHandler}
        rangePosition={rangePosition}
        onRangePositionChange={setRangePosition}
        shouldDisableDate={shouldDisableDate}
        renderInput={(startProps, endProps) => (
          <>
            <WaeStandardTextField
              {...startProps}
              fullWidth
              error={!!shouldShowStartError}
              helperText={shouldShowStartError ? intl.formatMessage({ id: startError }) : ''}
              onClick={() => {
                setRangePosition(RANGE_POSITIONS.START);
                setIsOpen(true);
              }}
              sx={{
                marginRight: theme.spacing(1),
              }}
              InputProps={{
                ...startProps.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarTodayOutlinedIcon
                      sx={{
                        color: theme?.textField?.inputLabel?.iconColor?.enabled,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <WaeStandardTextField
              {...endProps}
              fullWidth
              error={!!shouldShowEndError}
              helperText={shouldShowEndError ? intl.formatMessage({ id: endError }) : ''}
              onClick={() => {
                setRangePosition(RANGE_POSITIONS.END);
                setIsOpen(true);
              }}
              InputProps={{
                ...endProps.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarTodayOutlinedIcon
                      sx={{
                        color: theme?.textField?.inputLabel?.iconColor?.enabled,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </>
        )}
      />
    </LocalizationProvider>
  );
};

JobDateRangePicker.propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func,
    value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string])),
  }),
  meta: PropTypes.shape({
    submitFailed: PropTypes.bool,
    modified: PropTypes.bool,
    touched: PropTypes.bool,
  }),
  maxDaysAfterStart: PropTypes.number,
  getStartError: PropTypes.func,
  getEndError: PropTypes.func,
};

export default JobDateRangePicker;
